import moment from 'moment'
import React, { FC, useMemo } from 'react'

import { Chart, Settings, LIGHT_THEME, PartialTheme, BarSeries, Axis, Position, ScaleType } from '@elastic/charts'
import { EuiPanel, EuiTitle, EuiText, EuiFlexGroup, EuiFlexItem, EuiBadge, EuiSpacer, EuiButtonIcon, useGeneratedHtmlId } from '@elastic/eui'

import { AIPlan, AIPlanCustomSection } from 'api/interfaces/AIPlan'
import { AIPlanCampaignBuilder, AIPlanCampaignBuilderType, AIPlanStub } from 'api/interfaces/AIPlan'
import { formatCurrency, formatNumber, formatPercentage } from 'utils/Formatters'

export interface AIPlanDeliveryMetricsProps {
  aiPlan: AIPlan
  stub: AIPlanStub
  deliveryMetricsRef?: React.RefObject<HTMLDivElement>
  section?: AIPlanCustomSection
  editMode?: boolean
  onEdit?: () => void
}

interface MetricData {
  channelName: string
  offAdCritterName?: string
  color?: string
  isCpc?: boolean
}

interface MonthlyData {
  month: string
  cpm: number | null
  cpc: number | null
  min: number | null
  projected: number | null
  graphProjected: number | null
  max: number | null
  graphMax: number | null
  marginOfError: number | null
}

type RateAccessor = (builder: AIPlanCampaignBuilder) => number | undefined
type DailyVolumeCalculator = (dailyBudget: number, rate: number) => number

const cpmRate: RateAccessor = builder => builder.cpm
const cpcRate: RateAccessor = builder => builder.cpc
const impressionsFromCpm: DailyVolumeCalculator = (dailyBudget, cpm) => (dailyBudget / cpm) * 1000
const clicksFromCpc: DailyVolumeCalculator = (dailyBudget, cpc) => dailyBudget / cpc

const calculateAverageRate = (builders: AIPlanCampaignBuilder[], getRate: RateAccessor) => {
  const buildersWithRate = builders.filter(builder => {
    const rate = getRate(builder)
    return rate && rate > 0
  })
  if (buildersWithRate.length === 0) {
    return null
  }
  return buildersWithRate.reduce((sum, builder) => sum + getRate(builder)!, 0) / buildersWithRate.length
}

const calculateMonthlyBudget = (campaign: AIPlanCampaignBuilder, daysInMonth: number): number => {
  if (!campaign.intervalUnit || !campaign.intervalFrequency) {
    return campaign.intervalBudget
  }

  switch (campaign.intervalUnit) {
    case 'Day':
      return (campaign.intervalBudget / campaign.intervalFrequency) * daysInMonth
    case 'Week':
      return (campaign.intervalBudget / campaign.intervalFrequency / 7) * daysInMonth
    case 'Month':
      return campaign.intervalBudget / campaign.intervalFrequency
    default:
      return 0
  }
}

const getEndDate = (campaign: AIPlanCampaignBuilder): moment.Moment | undefined => {
  if (!campaign.startDate) {
    return undefined
  }

  if (campaign.endDate) {
    return moment(campaign.endDate)
  }

  if (!campaign.intervalUnit || !campaign.intervalFrequency || !campaign.intervalDuration) {
    return undefined
  }

  let momentUnit = 'month'
  if (campaign.intervalUnit === 'Day') momentUnit = 'day'
  else if (campaign.intervalUnit === 'Week') momentUnit = 'week'

  return moment(campaign.startDate).add(momentUnit as moment.unitOfTime.DurationConstructor, campaign.intervalDuration * campaign.intervalFrequency)
}

const calculateMonthlyVolume = (campaigns: AIPlanCampaignBuilder[], monthMoment: moment.Moment, getRate: RateAccessor, calcDaily: DailyVolumeCalculator) => {
  return campaigns.reduce((total, campaign) => {
    const rate = getRate(campaign)
    if (rate && rate > 0 && campaign.intervalBudget) {
      const campaignStart = moment(campaign.startDate)
      const campaignEnd = getEndDate(campaign)

      if (!campaignEnd) {
        return total
      }

      const daysInMonth = monthMoment.daysInMonth()
      const monthStart = monthMoment.clone().startOf('month')
      const monthEnd = monthMoment.clone().endOf('month')

      const effectiveStart = moment.max(campaignStart.clone().startOf('day'), monthStart)
      const effectiveEnd = moment.min(campaignEnd.clone().endOf('day'), monthEnd)

      if (effectiveEnd.isBefore(effectiveStart)) {
        return total
      }

      const activeDaysInMonth = effectiveEnd.diff(effectiveStart, 'days') + 1
      const monthlyBudget = calculateMonthlyBudget(campaign, daysInMonth)
      const dailyBudget = monthlyBudget / daysInMonth
      const dailyVolume = calcDaily(dailyBudget, rate)

      return total + dailyVolume * activeDaysInMonth
    }
    return total
  }, 0)
}

const emptyMonthlyData = (monthFull: string): MonthlyData => ({
  month: monthFull, cpm: null, cpc: null, min: null, projected: null, graphProjected: null, max: null, graphMax: null, marginOfError: null
})

const generateMonthlyRateData = (
  campaignBuilders: AIPlanCampaignBuilder[],
  marginOfError: number,
  getRate: RateAccessor,
  calcDaily: DailyVolumeCalculator,
  rateField: 'cpm' | 'cpc'
): MonthlyData[] => {
  const buildersWithRate = campaignBuilders.filter(builder => {
    const rate = getRate(builder)
    return rate && rate > 0
  })
  if (buildersWithRate.length === 0) {
    return []
  }

  const earliestStart = campaignBuilders.reduce((earliest, builder) => {
    const startDate = moment(builder.startDate)
    return !earliest || startDate.isBefore(earliest) ? startDate : earliest
  }, null as moment.Moment | null)

  const latestEnd = campaignBuilders.reduce((latest, builder) => {
    const endDate = getEndDate(builder)
    if (!endDate) return latest
    return !latest || endDate.isAfter(latest) ? endDate : latest
  }, null as moment.Moment | null)

  if (!earliestStart || !latestEnd) {
    return []
  }

  const totalMonths = Math.max(12, latestEnd.diff(earliestStart, 'months') + 1)
  const months: MonthlyData[] = []

  for (let i = 0; i < totalMonths; i++) {
    const monthMoment = moment(earliestStart).add(i, 'months')
    const monthFull = monthMoment.format('MMMM YYYY')

    const activeCampaigns = campaignBuilders.filter(builder => {
      const builderStart = moment(builder.startDate)
      const builderEnd = getEndDate(builder)
      return monthMoment.isBetween(builderStart, builderEnd, 'month', '[]')
    })

    if (activeCampaigns.length === 0) {
      months.push(emptyMonthlyData(monthFull))
      continue
    }

    const activeCampaignsWithRate = activeCampaigns.filter(builder => {
      const rate = getRate(builder)
      return rate && rate > 0
    })

    if (activeCampaignsWithRate.length === 0) {
      months.push(emptyMonthlyData(monthFull))
      continue
    }

    const monthlyAverageRate = calculateAverageRate(activeCampaignsWithRate, getRate)

    if (!monthlyAverageRate) {
      months.push(emptyMonthlyData(monthFull))
      continue
    }

    const projected = calculateMonthlyVolume(activeCampaignsWithRate, monthMoment, getRate, calcDaily)

    const obj: MonthlyData = {
      ...emptyMonthlyData(monthFull),
      [rateField]: monthlyAverageRate,
      projected: Math.round(projected),
      min: Math.round(projected - projected * marginOfError),
      max: Math.round(projected + projected * marginOfError),
      marginOfError: marginOfError
    }

    obj.graphProjected = Math.round(obj.projected! - obj.min!)
    obj.graphMax = Math.round(obj.max! - obj.projected!)

    months.push(obj)
  }

  return months
}

const generateMonthlyData = (builders: AIPlanCampaignBuilder[], marginOfError: number) =>
  generateMonthlyRateData(builders, marginOfError, cpmRate, impressionsFromCpm, 'cpm')

const generateMonthlyCpcData = (builders: AIPlanCampaignBuilder[], marginOfError: number) =>
  generateMonthlyRateData(builders, marginOfError, cpcRate, clicksFromCpc, 'cpc')

const AIPlanDeliveryMetrics: FC<AIPlanDeliveryMetricsProps> = ({ aiPlan, stub, deliveryMetricsRef, section, editMode, onEdit }) => {
  const impressionChartId = useGeneratedHtmlId()
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth)

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const impressionDistributionInternetData = useMemo(() => {
    if (!stub?.id || !aiPlan.internetMarginOfError) {
      return []
    }
    return generateMonthlyData(
      stub.campaignBuilders.filter(builder => builder.campaignType === AIPlanCampaignBuilderType.Internet),
      aiPlan.internetMarginOfError
    )
  }, [stub?.id, stub.campaignBuilders, aiPlan.internetMarginOfError])

  const impressionDistributionTvData = useMemo(() => {
    if (!stub?.id || !aiPlan.tvMarginOfError) {
      return []
    }
    return generateMonthlyData(
      stub.campaignBuilders.filter(builder => builder.campaignType === AIPlanCampaignBuilderType.TV),
      aiPlan.tvMarginOfError
    )
  }, [stub?.id, stub.campaignBuilders, aiPlan.tvMarginOfError])

  const impressionDistributionAudioData = useMemo(() => {
    if (!stub?.id || !aiPlan.audioMarginOfError) {
      return []
    }
    return generateMonthlyData(
      stub.campaignBuilders.filter(builder => builder.campaignType === AIPlanCampaignBuilderType.Audio),
      aiPlan.audioMarginOfError
    )
  }, [stub?.id, stub.campaignBuilders, aiPlan.audioMarginOfError])

  const impressionDistributionBillboardData = useMemo(() => {
    if (!stub?.id || !aiPlan.billboardMarginOfError) {
      return []
    }
    return generateMonthlyData(
      stub.campaignBuilders.filter(builder => builder.campaignType === AIPlanCampaignBuilderType.Billboard),
      aiPlan.billboardMarginOfError
    )
  }, [stub?.id, stub.campaignBuilders, aiPlan.billboardMarginOfError])

  const impressionDistributionOffAdCritterData = useMemo(() => {
    if (!stub?.id) {
      return new Map<string, MonthlyData[]>()
    }

    const offAdCritterCampaigns = stub.campaignBuilders.filter(builder => builder.campaignType === AIPlanCampaignBuilderType.OffAdCritter && builder.includeInProjections && builder.cpm && builder.cpm > 0 && builder.minCpm && builder.maxCpm && builder.marginOfError)

    const groupedByPlatform = new Map<string, AIPlanCampaignBuilder[]>()
    offAdCritterCampaigns.forEach(campaign => {
      const key = campaign.offAdCritterName || campaign.id
      const group = groupedByPlatform.get(key) || []
      group.push(campaign)
      groupedByPlatform.set(key, group)
    })

    const dataMap = new Map<string, MonthlyData[]>()
    groupedByPlatform.forEach((campaigns, platformName) => {
      const avgMarginOfError = campaigns.reduce((sum, c) => sum + c.marginOfError!, 0) / campaigns.length
      const monthlyData = generateMonthlyData(campaigns, avgMarginOfError)
      if (monthlyData.length > 0) {
        dataMap.set(platformName, monthlyData)
      }
    })

    return dataMap
  }, [stub?.id, stub.campaignBuilders])

  const clickDistributionCpcData = useMemo(() => {
    if (!stub?.id) {
      return new Map<string, MonthlyData[]>()
    }

    const cpcCampaigns = stub.campaignBuilders.filter(builder => builder.campaignType === AIPlanCampaignBuilderType.OffAdCritter && builder.includeInProjections && builder.cpc && builder.cpc > 0 && builder.minCpc && builder.maxCpc && builder.marginOfError)

    const groupedByPlatform = new Map<string, AIPlanCampaignBuilder[]>()
    cpcCampaigns.forEach(campaign => {
      const key = campaign.offAdCritterName || campaign.id
      const group = groupedByPlatform.get(key) || []
      group.push(campaign)
      groupedByPlatform.set(key, group)
    })

    const dataMap = new Map<string, MonthlyData[]>()
    groupedByPlatform.forEach((campaigns, platformName) => {
      const avgMarginOfError = campaigns.reduce((sum, c) => sum + c.marginOfError!, 0) / campaigns.length
      const monthlyData = generateMonthlyCpcData(campaigns, avgMarginOfError)
      if (monthlyData.length > 0) {
        dataMap.set(platformName, monthlyData)
      }
    })

    return dataMap
  }, [stub?.id, stub.campaignBuilders])

  const chartTheme: PartialTheme = {
    axes: {
      tickLabel: {
        fontSize: 12,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
      }
    },
    colors: {
      vizColors: ['transparent', '#d47a3a', '#d47a3a']
    }
  }

  const metricsData: MetricData[] = useMemo(() => {
    const standardPlatforms = ['Google', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok']
    const metrics: MetricData[] = []

    if (impressionDistributionTvData.length > 0) {
      metrics.push({ channelName: 'Connected TV' })
    }

    if (impressionDistributionAudioData.length > 0) {
      metrics.push({ channelName: 'Streaming Audio' })
    }

    if (impressionDistributionInternetData.length > 0) {
      metrics.push({ channelName: 'Internet' })
    }

    const placedPlatforms = new Set<string>()

    standardPlatforms.forEach(platformName => {
      if (impressionDistributionOffAdCritterData.has(platformName)) {
        const firstCampaign = stub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channelName: platformName,
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor
        })
        placedPlatforms.add(platformName)
      } else if (clickDistributionCpcData.has(platformName)) {
        const firstCampaign = stub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channelName: platformName,
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor,
          isCpc: true
        })
        placedPlatforms.add(platformName)
      }
    })

    if (impressionDistributionBillboardData.length > 0) {
      metrics.push({ channelName: 'Digital Billboard' })
    }

    impressionDistributionOffAdCritterData.forEach((_, platformName) => {
      if (!placedPlatforms.has(platformName)) {
        const firstCampaign = stub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channelName: platformName,
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor
        })
        placedPlatforms.add(platformName)
      }
    })

    clickDistributionCpcData.forEach((_, platformName) => {
      if (!placedPlatforms.has(platformName)) {
        const firstCampaign = stub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channelName: platformName,
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor,
          isCpc: true
        })
      }
    })

    return metrics
  }, [impressionDistributionTvData.length, impressionDistributionAudioData.length, impressionDistributionInternetData.length, impressionDistributionBillboardData.length, impressionDistributionOffAdCritterData, clickDistributionCpcData, stub?.campaignBuilders])

  const tilesPerRow = windowWidth < 1660 ? 2 : 3

  const organizeMetricsIntoRows = (metrics: MetricData[], perRow: number): MetricData[][] => {
    const totalItems = metrics.length

    if (totalItems === 0) {
      return []
    }

    if (totalItems <= perRow) {
      return [metrics]
    }

    if (totalItems === 4 && perRow === 2) {
      return [metrics.slice(0, 2), metrics.slice(2, 4)]
    }

    const fullRows = Math.floor(totalItems / perRow)
    const remainder = totalItems % perRow
    const rows: MetricData[][] = []

    if (remainder === 0) {
      for (let i = 0; i < fullRows; i++) {
        rows.push(metrics.slice(i * perRow, (i + 1) * perRow))
      }
      return rows
    }

    if (remainder === 1 && perRow === 3) {
      for (let i = 0; i < fullRows - 1; i++) {
        rows.push(metrics.slice(i * perRow, (i + 1) * perRow))
      }
      const remaining = metrics.slice((fullRows - 1) * perRow)
      rows.push(remaining.slice(0, 2))
      rows.push(remaining.slice(2, 4))
      return rows
    }

    for (let i = 0; i < fullRows; i++) {
      rows.push(metrics.slice(i * perRow, (i + 1) * perRow))
    }
    if (remainder > 0) {
      rows.push(metrics.slice(fullRows * perRow))
    }

    return rows
  }

  const metricsRows = useMemo(() => organizeMetricsIntoRows(metricsData, tilesPerRow), [metricsData, tilesPerRow])

  const yAccessorFn = (d: MonthlyData) => d.graphProjected
  yAccessorFn.fieldName = 'Projected'

  if (section?.hideAll === true) {
    return null
  }

  const sectionTitle = section?.title ?? 'CPM and Impressions'
  const sectionContent = section?.content ?? 'CPM and impression projections are based on real-time publisher data and historic campaign performance. The noted margin of error is due to inherent cost variation in digital ad auctions.'
  const showTitle = section?.showTitle ?? true

  return (
    <EuiPanel paddingSize='l'>
      <EuiFlexGroup alignItems='center' gutterSize='s'>
        {showTitle && (
          <EuiFlexItem grow={false}>
            <EuiTitle size='m'>
              <h2>{sectionTitle}</h2>
            </EuiTitle>
          </EuiFlexItem>
        )}
        {editMode && onEdit && (
          <EuiFlexItem grow={false}>
            <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEdit} aria-label='Edit CPM and Impressions section' />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      <EuiSpacer size='s' />
      <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
        {sectionContent}
      </EuiText>
      <EuiSpacer size='l' />

      <div ref={deliveryMetricsRef}>
        {metricsRows.map((row, rowIndex) => (
          <React.Fragment key={`row-${rowIndex}`}>
            <EuiFlexGroup gutterSize='l' wrap={false}>
              {row.map((metric, metricIndex) => {
                const index = metricsRows.slice(0, rowIndex).reduce((sum, r) => sum + r.length, 0) + metricIndex
                let distributionData: MonthlyData[] = []

                if (metric.isCpc && metric.offAdCritterName) {
                  distributionData = clickDistributionCpcData.get(metric.offAdCritterName) || []
                } else if (metric.offAdCritterName) {
                  distributionData = impressionDistributionOffAdCritterData.get(metric.offAdCritterName) || []
                } else if (metric.channelName === 'Connected TV') {
                  distributionData = impressionDistributionTvData
                } else if (metric.channelName === 'Streaming Audio') {
                  distributionData = impressionDistributionAudioData
                } else if (metric.channelName === 'Internet') {
                  distributionData = impressionDistributionInternetData
                } else if (metric.channelName === 'Digital Billboard') {
                  distributionData = impressionDistributionBillboardData
                }

                let targetRate = 0
                let marginOfError = 0

                if (distributionData.length > 0) {
                  if (metric.isCpc) {
                    const validDataPoints = distributionData.filter(d => d.cpc && d.marginOfError)
                    if (validDataPoints.length > 0) {
                      targetRate = validDataPoints.reduce((sum, d) => sum + (d.cpc || 0), 0) / validDataPoints.length
                      marginOfError = validDataPoints[0].marginOfError || 0
                    }
                  } else {
                    const validDataPoints = distributionData.filter(d => d.cpm && d.marginOfError)
                    if (validDataPoints.length > 0) {
                      targetRate = validDataPoints.reduce((sum, d) => sum + (d.cpm || 0), 0) / validDataPoints.length
                      marginOfError = validDataPoints[0].marginOfError || 0
                    }
                  }
                }

                const totalMin = distributionData.reduce((min, d) => min + (d.min || 0), 0)
                const totalProjected = distributionData.reduce((projected, d) => projected + (d.projected || 0), 0)
                const totalMax = distributionData.reduce((max, d) => max + (d.max || 0), 0)

                const maxValue = distributionData.reduce((max, d) => Math.max(max, d.max || 0), 0)

                const rateLabel = metric.isCpc ? 'Projected Avg. CPC' : 'Projected Avg. CPM'
                const totalLabel = metric.isCpc ? 'Total Projected Clicks' : metric.channelName === 'Internet' ? 'Total Projected Impressions' : metric.channelName === 'Streaming Audio' ? 'Total Projected Plays' : 'Total Projected Views'
                const chartLabel = metric.isCpc ? 'Clicks by Month' : metric.channelName === 'Internet' ? 'Impression by Month' : metric.channelName === 'Streaming Audio' ? 'Plays by Month' : 'Views by Month'

                return (
                  <EuiFlexItem key={index} grow={3}>
                    <EuiPanel
                      paddingSize='l'
                      hasShadow={true}
                      style={{
                        height: '100%',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                      }}>
                      <EuiTitle size='xs'>
                        <h3>{metric.channelName}</h3>
                      </EuiTitle>

                      <EuiSpacer size='m' />

                      <EuiFlexGroup alignItems='baseline'>
                        <EuiFlexItem grow={false}>
                          <EuiText size='s'>{rateLabel}</EuiText>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                          <EuiBadge color='#F0B051'>
                            <EuiText size='m' style={{ fontWeight: 'bold' }}>
                              {formatCurrency(targetRate)}
                            </EuiText>
                          </EuiBadge>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                          <EuiText size='xs' color='subdued'>
                            ±{formatPercentage(marginOfError)}
                          </EuiText>
                        </EuiFlexItem>
                      </EuiFlexGroup>

                      <EuiSpacer size='m' />

                      <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        {totalLabel}
                      </EuiText>
                      <EuiFlexGroup gutterSize='s'>
                        <EuiFlexItem>
                          <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)' }}>
                            <EuiText size='xs' color='subdued'>
                              Minimum
                            </EuiText>
                            <EuiText size='m' color='subdued'>
                              {formatNumber(totalMin, 0, 0)}
                            </EuiText>
                          </EuiPanel>
                        </EuiFlexItem>
                        <EuiFlexItem>
                          <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)', borderStyle: 'solid', borderColor: '#F0B051' }}>
                            <EuiText size='xs' style={{ fontWeight: 'bold' }}>
                              Projected
                            </EuiText>
                            <EuiText size='m' style={{ fontWeight: 'bold' }}>
                              {formatNumber(totalProjected, 0, 0)}
                            </EuiText>
                          </EuiPanel>
                        </EuiFlexItem>
                        <EuiFlexItem>
                          <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)' }}>
                            <EuiText size='xs' color='subdued'>
                              Maximum
                            </EuiText>
                            <EuiText size='m' color='subdued'>
                              {formatNumber(totalMax, 0, 0)}
                            </EuiText>
                          </EuiPanel>
                        </EuiFlexItem>
                      </EuiFlexGroup>

                      <EuiSpacer size='m' />

                      <div>
                        <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                          {chartLabel}
                        </EuiText>
                        <EuiPanel paddingSize='s' style={{ backgroundColor: 'var(--euiColorEmptyShade)' }}>
                          <div style={{ height: '300px', position: 'relative' }}>
                            <Chart id={impressionChartId + index} size={{ height: 300 }}>
                              <Settings
                                theme={chartTheme}
                                baseTheme={LIGHT_THEME}
                                showLegend={false}
                                tooltip={{
                                  customTooltip: ({ values }) => {
                                    const dataPoint = values.find(v => (v.seriesIdentifier as any).yAccessor === 'Projected')
                                    if (!dataPoint) {
                                      return null
                                    }

                                    const monthlyData = dataPoint.datum as MonthlyData

                                    if (!monthlyData.projected || !monthlyData.min || !monthlyData.max) {
                                      return null
                                    }

                                    return (
                                      <EuiPanel paddingSize='s' hasShadow={true} style={{ minWidth: '140px' }}>
                                        <EuiTitle size='s'>
                                          <h3 style={{ marginBottom: '4px' }}>{monthlyData.month}</h3>
                                        </EuiTitle>
                                        <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                          Projected: {formatNumber(monthlyData.projected, 0, 0)}
                                        </EuiText>
                                        <EuiText size='xs' color='subdued'>
                                          Min: {formatNumber(monthlyData.min, 0, 0)}
                                        </EuiText>
                                        <EuiText size='xs' color='subdued'>
                                          Max: {formatNumber(monthlyData.max, 0, 0)}
                                        </EuiText>
                                      </EuiPanel>
                                    )
                                  }
                                }}
                              />
                              <BarSeries
                                id='Projected'
                                data={distributionData}
                                xAccessor='month'
                                yAccessors={['min', yAccessorFn, 'graphMax']}
                                stackAccessors={['min', yAccessorFn, 'graphMax']}
                                xScaleType={ScaleType.Ordinal}
                                yScaleType={ScaleType.Linear}
                                tickFormat={value => formatNumber(value, 0, 0)}
                                filterSeriesInTooltip={(d: any) => d.seriesKeys.includes('Projected')}
                                barSeriesStyle={{
                                  rectBorder: {
                                    visible: true,
                                    stroke: 'transparent',
                                    strokeWidth: 0.5
                                  }
                                }}
                              />
                              <Axis id='impressions' position={Position.Left} tickFormat={value => formatNumber(value, 0, 0)} domain={{ min: 0, max: maxValue + maxValue * 0.2 }} />
                              <Axis
                                id='months'
                                position={Position.Bottom}
                                style={{
                                  tickLabel: {
                                    rotation: 0
                                  }
                                }}
                                labelFormat={d => d.charAt(0)}
                              />
                            </Chart>
                          </div>
                        </EuiPanel>
                      </div>
                    </EuiPanel>
                  </EuiFlexItem>
                )
              })}
            </EuiFlexGroup>
            {rowIndex < metricsRows.length - 1 && <EuiSpacer size='l' />}
          </React.Fragment>
        ))}
      </div>

      <EuiSpacer size='l' />
    </EuiPanel>
  )
}

export default AIPlanDeliveryMetrics
